import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Preloader from '../components/Preloader';
import SearchableSelect from '../components/SearchableSelect';
import { Navigate } from 'react-router-dom';
import { useCheckUserRole } from './hooks/useCheckUserRole';

const YerelKapanis = () => {
    const { userRole, isLoggedIn, subRole, userId } = useCheckUserRole();

    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({});
    const [filterModes, setFilterModes] = useState({});
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const observer = useRef();

    // Removed Bookmaker state
    const [selectedDate, setSelectedDate] = useState('');
    const [matchesForDropdown, setMatchesForDropdown] = useState([]);
    const [selectedMatchId, setSelectedMatchId] = useState('');

    const [savedFilters, setSavedFilters] = useState([]);
    const [selectedFilterId, setSelectedFilterId] = useState('');

    const [showSettings, setShowSettings] = useState(false);
    const [columnGroups, setColumnGroups] = useState([]);

    // --- State: Design Settings ---
    const defaultColors = {
        headerBg: '#0a1b2a',
        headerText: '#F6F1AD',
        rowOddBg: '#1f2d3d',
        rowEvenBg: '#152232',
        rowText: '#F6F1AD',
        hoverBg: '#2a4a6a'
    };

    const colorLabels = {
        headerBg: 'Başlık Arkaplanı',
        headerText: 'Başlık Yazısı',
        rowOddBg: 'Satır (Tek) Arkaplanı',
        rowEvenBg: 'Satır (Çift) Arkaplanı',
        rowText: 'Satır Yazısı',
        hoverBg: 'Üzerine Gelince'
    };

    const [colors, setColors] = useState(() => {
        const savedColors = localStorage.getItem('yerelKapanisColors'); // Changed storage key
        return savedColors ? JSON.parse(savedColors) : defaultColors;
    });

    // --- Data Fetching: Init ---
    const generateDateOptions = () => {
        const options = [];
        const todayDate = new Date();
        const dayLabels = ['Bugün', 'Yarın', 'Ertesi Gün'];
        for (let i = 0; i <= 2; i++) {
            const d = new Date(todayDate);
            d.setDate(todayDate.getDate() + i);
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            const dateStr = `${day}.${month}.${year}`;
            options.push({ value: dateStr, label: `${dayLabels[i]} (${dateStr})` });
        }
        // Kullanıcının testi için eklendi:
        options.push({ value: '19.08.2025', label: 'Test 20250819 (19.08.2025)' });
        return options;
    };
    const [dateOptions] = useState(generateDateOptions());

    // --- Column Modal State ---
    const [showColumnModal, setShowColumnModal] = useState(false);
    const [allPotentialGroups, setAllPotentialGroups] = useState([]);
    const [tempSelectedIds, setTempSelectedIds] = useState([]);

    // Fetch all potential groups for the modal (userId=0)
    useEffect(() => {
        const fetchAllGroups = async () => {
            try {
                // Modified to 'M'
                const response = await fetch(`/api/Columns/Get/M/0`);
                if (response.ok) {
                    const data = await response.json();
                    if (data.isSuccess) setAllPotentialGroups(data.groups);
                }
            } catch (err) { console.error(err); }
        };
        fetchAllGroups();
    }, []);

    // Reusable fetch columns function
    const fetchColumns = useCallback(async () => {
        if (!userId) return;
        try {
            // Modified to 'M'
            const response = await fetch(`/api/Columns/Get/M/${userId}`);
            if (response.ok) {
                const data = await response.json();
                if (data.isSuccess) {
                    setColumnGroups(data.groups);
                }
            }
        } catch (err) {
            console.error("Failed to fetch columns", err);
        }
    }, [userId]);

    useEffect(() => {
        fetchColumns();
    }, [fetchColumns]);

    // Helper to ensure comparison is safe (string vs int)
    const intId = (val) => parseInt(val, 10);

    const openColumnModal = () => {
        // Init temp selection with currently active groups
        const currentIds = columnGroups.map(g => g.id);
        // Ensure ID 1 is included
        if (!currentIds.includes(1)) currentIds.push(1);
        setTempSelectedIds(currentIds);
        setShowColumnModal(true);
    };

    const defaultGroupId = useMemo(() => {
        if (allPotentialGroups.length > 0) return intId(allPotentialGroups[0].id);
        return 1;
    }, [allPotentialGroups]);

    const toggleGroupSelection = (id) => {
        const iId = intId(id);
        if (iId === defaultGroupId) return; // Prevent toggling default group

        if (tempSelectedIds.includes(iId)) {
            setTempSelectedIds(tempSelectedIds.filter(x => x !== iId));
        } else {
            setTempSelectedIds([...tempSelectedIds, iId]);
        }
    };

    const handleSelectAll = () => {
        setTempSelectedIds(allPotentialGroups.map(g => intId(g.id)));
    };

    const handleDeselectAll = () => {
        // Always keep default group
        setTempSelectedIds([defaultGroupId]);
    };

    const handleSaveColumnConfig = async () => {
        if (!userId) {
            alert("Kullanıcı kimliği bulunamadı.");
            return;
        }
        const idStr = tempSelectedIds.join(',');
        try {
            // Modified to 'M'
            const response = await fetch(`/api/Columns/Save/M/${userId}/${idStr}`, {
                method: 'POST'
            });
            const data = await response.json();
            if (data.isSuccess) {
                alert("Kolon ayarları kaydedildi ve güncellendi.");
                setShowColumnModal(false);
                fetchColumns(); // Update the main table immediately
            } else {
                alert("Hata: " + data.message);
            }
        } catch (err) {
            console.error(err);
        }
    };



    // --- Saved Filters ---
    const fetchSavedFilters = useCallback(async () => {
        try {
            // You might want a different filter type for Mackolik?
            // User didn't specify, but let's use type 3 or stick to 2 if shared?
            // "KapanisTablo" used 2. Let's assume unique filters if we want separate list.
            // If user wants separation, we should use a different type like 3.
            // But code says "tamamen aynisi".
            // Let's stick to 2 but maybe filters won't work across tables if keys differ.
            // But keys might be similar.
            // Let's update filter_type to 3 just to be safe and clean.
            const response = await fetch('/api/get_filters?filter_type=5');
            if (response.ok) {
                const data = await response.json();
                setSavedFilters(data);
            }
        } catch (error) {
            console.error("Filters fetch error:", error);
        }
    }, []);

    useEffect(() => {
        fetchSavedFilters();
    }, [fetchSavedFilters]);

    const handleSaveSavedFilter = async () => {
        const filterName = prompt("Filtre adı giriniz:");
        if (!filterName) return;

        const filterDataToSave = {
            filters: filters
        };

        try {
            const response = await fetch('/api/save-filter', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    filter_name: filterName,
                    filter_data: filterDataToSave,
                    filter_type: 5 // Using 3 for Mackolik
                }),
            });

            if (response.ok) {
                alert("Filtre başarıyla kaydedildi.");
                fetchSavedFilters();
            } else {
                const err = await response.json();
                alert("Hata: " + (err.error || "Kaydedilemedi"));
            }
        } catch (error) {
            console.error("Save error:", error);
            alert("Bir hata oluştu.");
        }
    };

    const handleDeleteSavedFilter = async () => {
        if (!selectedFilterId) return;
        if (!window.confirm("Bu filtreyi silmek istediğinize emin misiniz?")) return;

        try {
            const response = await fetch('/api/delete-filter', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: selectedFilterId }),
            });

            if (response.ok) {
                alert("Filtre silindi.");
                setSelectedFilterId("");
                fetchSavedFilters();
            } else {
                alert("Silinemedi.");
            }
        } catch (e) {
            console.error(e);
            alert("Hata oluştu.");
        }
    };

    // --- Match Fetching ---

    // Fetch matches for DROPDOWN when date changes
    useEffect(() => {
        const fetchDropdownMatches = async () => {
            // Reset selections on date change
            setSelectedMatchId('');
            setSelectedFilterId('');

            if (!selectedDate) {
                setMatchesForDropdown([]);
                return;
            }

            try {
                const parts = selectedDate.split('.');
                let formattedDate = selectedDate;
                if (parts.length === 3) {
                    formattedDate = `${parts[2]}${parts[1]}${parts[0]}`; // YYYYMMDD
                }

                // Modified to 'M'
                const response = await fetch(`/api/Match/Get/M/${formattedDate}`);
                const data = await response.json();
                if (data.isSuccess) {
                    setMatchesForDropdown(data.matches);
                }
            } catch (e) {
                console.error(e);
            }
        };
        fetchDropdownMatches();
    }, [selectedDate]);

    // Refactored helper to apply a filter (by ID) given a specific match ID
    const checkAndApplyFilter = (filterId, matchId) => {
        if (!filterId) return;
        const savedF = savedFilters.find(f => f.id.toString() === filterId.toString());
        if (!savedF) return;

        let pData = savedF.filter_data;
        if (typeof pData === 'string') {
            try { pData = JSON.parse(pData); } catch (ex) { }
        }

        if (pData.match_date && !selectedDate) setSelectedDate(pData.match_date);

        let filtersToSet = pData.filters;
        filtersToSet = filtersToSet || {};

        // If a match is selected, overlay its values onto the filter
        if (matchId && matchesForDropdown.length > 0) {
            const selectedMatch = matchesForDropdown.find(m => m.id.toString() === matchId.toString());
            if (selectedMatch && selectedMatch.full_data) {
                filtersToSet = { ...filtersToSet };

                // Comprehensive Blacklist of columns to EXCLUDE from "Same Match" filtering
                const blacklist = [
                    'id', 'match_id', 'sys_match_id',
                    'match_date', 'date', 'tarih', 'match_time', 'time', 'saat', 'mac_saati', 'mac_tarihi',
                    'home_team', 'away_team', 'team_home', 'team_away', 'year', 'yil',
                    'ev_sahibi', 'deplasman', 'ev', 'misafir', 'ev_takim', 'misafir_takim',
                    'home_name', 'away_name', 'home', 'away', 'match_name', 'mac_adi',
                    'league', 'lig', 'league_name', 'lig_adi', 'country', 'ulke', 'country_name',
                    'created_at', 'updated_at', 'inserted_at'
                ];

                // 1. Remove blacklisted keys from the Saved Filter explicitly
                blacklist.forEach(b => delete filtersToSet[b]);

                // 2. Update filter with match data, BUT SKIP blacklisted columns
                Object.keys(filtersToSet).forEach(key => {
                    if (blacklist.includes(key)) return;

                    const val = selectedMatch.full_data[key];
                    if (val !== undefined && val !== null) {
                        filtersToSet[key] = String(val);
                    }
                });
            }
        }
        setFilters(filtersToSet);
    };

    // Handle Match Selection from Dropdown
    const handleMatchSelect = async (e) => {
        const mId = e.target.value;
        setSelectedMatchId(mId);

        if (!mId) return;

        // If a filter is currently active, re-apply it with the new match's data
        if (selectedFilterId) {
            checkAndApplyFilter(selectedFilterId, mId);
        }

        // Trigger Update
        try {
            // Modified to 'M'
            const response = await fetch(`/api/Match/Update/M/${mId}`, { method: 'POST' });
            const data = await response.json();
            if (data.isSuccess) {
                // Success
                // Do not filter table by match_id, user wants to keep table state or use this match for other filters
                // handleFilterChange('sys_match_id', mId); 
            } else {
                alert("Hata: " + data.message);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const fetchMatches = useCallback(async (pageNum, currentFilters, currentModes, currentDate, signal) => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('limit', 50);
            params.append('page', pageNum);

            if (currentDate) {
                const parts = currentDate.split('.');
                if (parts.length === 3) {
                    const formatted = `${parts[2]}-${parts[1]}-${parts[0]}`;
                    params.append('match_date', formatted);
                } else if (currentDate.includes('-')) {
                    params.append('match_date', currentDate);
                }
            }

            Object.keys(currentFilters).forEach(key => {
                let val = currentFilters[key];
                if (val && val.trim() !== '') {
                    // Special handling for computed columns
                    if (key === 'first_half_score') {
                        if (val.includes('-')) {
                            const [h, a] = val.split('-');
                            params.append('home_ht_score', h.trim());
                            params.append('away_ht_score', a.trim());
                        } else {
                            // Fallback or partial search? Usually scores are exact. 
                            // If user types just '1', maybe they mean home? But '1-0' is standard.
                            // Let's assume standard format or do nothing.
                        }
                        return; // Skip default append
                    }

                    if (key === 'full_time_score') {
                        if (val.includes('-')) {
                            const [h, a] = val.split('-');
                            params.append('home_ft_score', h.trim());
                            params.append('away_ft_score', a.trim());
                        }
                        return;
                    }

                    if (key === 'ht_ft_result') {
                        if (val.includes('/')) {
                            const [ht, ft] = val.split('/');
                            params.append('iy', ht.trim().toUpperCase()); // Assuming column 'iy' exists or is handled
                            params.append('ms', ft.trim().toUpperCase()); // Assuming column 'ms' exists or is handled
                        } else {
                            // If user types just '1', maybe they mean MS? Or IY?
                            // Let's assume matches strict format for now or check if length 1
                            if (val.length === 1) {
                                params.append('ms', val.toUpperCase());
                            }
                        }
                        return;
                    }

                    const mode = currentModes[key] || 'default';

                    // Fix for date filtering
                    if (['match_date', 'match_time', 'match_d_t', 'date', 'Date', 'tarih'].includes(key)) {
                        if (val.includes('-')) {
                            const parts = val.split('-');
                            if (parts.length === 3) {
                                val = `${parts[2]}-${parts[1]}-${parts[0]}`;
                            }
                        } else if (val.includes('.')) {
                            const parts = val.split('.');
                            if (parts.length === 3) {
                                val = `${parts[2]}-${parts[1]}-${parts[0]}`;
                            }
                        }
                    }

                    if (mode === 'range') {
                        const parts = val.split('-');
                        if (parts.length === 2) {
                            params.append(key + '_min', parts[0].trim());
                            params.append(key + '_max', parts[1].trim());
                        } else {
                            params.append(key, val);
                        }
                    } else if (mode === 'lt') {
                        params.append(key + '_lt', val.trim());
                    } else if (mode === 'gt') {
                        params.append(key + '_gt', val.trim());
                    } else if (mode === 'eq') {
                        params.append(key + '_eq', val.trim());
                    } else {
                        params.append(key, val);
                    }
                }
            });

            // Modified to /api/mackolik
            const response = await fetch(`/api/mackolik?${params.toString()}`, { signal });
            if (!response.ok) throw new Error('Network response was not ok');

            const data = await response.json();

            setMatches(prev => pageNum === 1 ? data.data : [...prev, ...data.data]);

            // Fix: If no data returned, assume no more pages to prevent infinite loops
            if (!data.data || data.data.length === 0) {
                setHasMore(false);
            } else {
                setHasMore(data.has_next);
            }
        } catch (err) {
            if (err.name === 'AbortError') return;
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    // --- Effects ---
    // 1. Debounce Filters
    // 1. Debounce Filters
    useEffect(() => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
            setPage(1);

            // If filters are active, ignore selectedDate to allow global history search
            const hasActiveFilters = Object.values(filters).some(
                value => value != null && String(value).trim() !== ''
            );
            const dateToUse = hasActiveFilters ? null : selectedDate;

            fetchMatches(1, filters, filterModes, dateToUse, controller.signal);
        }, 1000);
        return () => {
            clearTimeout(timeoutId);
            controller.abort();
        };
    }, [filters, filterModes, selectedDate, fetchMatches]);

    // 2. Page Change
    useEffect(() => {
        const controller = new AbortController();
        if (page > 1) {
            const hasActiveFilters = Object.values(filters).some(
                value => value != null && String(value).trim() !== ''
            );
            fetchMatches(page, filters, filterModes, hasActiveFilters ? null : selectedDate, controller.signal);
        }
        return () => controller.abort();
    }, [page]);

    // 3. Infinite Scroll
    const lastMatchElementRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prevPage => prevPage + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore]);


    // --- Handlers ---
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleClearFilters = () => {
        setFilters({});
        setSelectedDate('');
        setSelectedMatchId('');
        setSelectedFilterId('');
    };

    const handleColorChange = (key, value) => {
        const newColors = { ...colors, [key]: value };
        setColors(newColors);
        localStorage.setItem('yerelKapanisColors', JSON.stringify(newColors));
    };

    const handleFilterModeChange = (key, mode) => {
        setFilterModes(prev => ({ ...prev, [key]: mode }));
    };

    const getFilterModeStyle = (mode) => {
        switch (mode) {
            case 'eq':
                return {
                    bg: '#d4edda',
                    color: '#155724',
                    border: '1px solid #c3e6cb',
                    shadow: '0 0 0 2px rgba(40, 167, 69, 0.2)'
                };
            case 'range':
                return {
                    bg: '#cce5ff',
                    color: '#004085',
                    border: '1px solid #b8daff',
                    shadow: '0 0 0 2px rgba(0, 123, 255, 0.2)'
                };
            case 'lt':
                return {
                    bg: '#fff3cd',
                    color: '#856404',
                    border: '1px solid #ffeeba',
                    shadow: '0 0 0 2px rgba(255, 193, 7, 0.2)'
                };
            case 'gt':
                return {
                    bg: '#f8d7da',
                    color: '#721c24',
                    border: '1px solid #f5c6cb',
                    shadow: '0 0 0 2px rgba(220, 53, 69, 0.2)'
                };
            default:
                return {
                    bg: 'rgba(255,255,255,0.1)',
                    color: colors.headerText,
                    border: '1px solid transparent',
                    shadow: 'none'
                };
        }
    };

    const getCellContent = (match, colName) => {
        if (!match) return '-';

        if (colName === 'first_half_score') {
            const h = match.home_ht_score;
            const a = match.away_ht_score;
            if (h !== undefined && h !== null && a !== undefined && a !== null) {
                return `${h}-${a}`;
            }
            return '-';
        }

        if (colName === 'full_time_score') {
            const h = match.home_ft_score;
            const a = match.away_ft_score;
            if (h !== undefined && h !== null && a !== undefined && a !== null) {
                return `${h}-${a}`;
            }
            return '-';
        }

        if (colName === 'ht_ft_result') {
            const hh = match.home_ht_score;
            const ah = match.away_ht_score;
            const hf = match.home_ft_score;
            const af = match.away_ft_score;

            if (hh !== undefined && hh !== null && ah !== undefined && ah !== null &&
                hf !== undefined && hf !== null && af !== undefined && af !== null) {

                let ht = 'X';
                if (parseInt(hh) > parseInt(ah)) ht = '1';
                else if (parseInt(hh) < parseInt(ah)) ht = '2';

                let ft = 'X';
                if (parseInt(hf) > parseInt(af)) ft = '1';
                else if (parseInt(hf) < parseInt(af)) ft = '2';

                return `${ht}/${ft}`;
            }
            return '-';
        }

        // Format date columns
        if (['match_date', 'match_time', 'match_d_t'].includes(colName)) {
            let val = match[colName];
            if (!val) return '-';

            // Handle ISO strings (e.g. 2026-01-05T23:15:00+03:00)
            if (typeof val === 'string' && val.includes('T')) {
                const parts = val.split('T');
                // If it is 'match_time', usually we just want time "HH:mm"
                if (colName === 'match_time') {
                    return parts[1].substring(0, 5); // 23:15
                }
                // For match_date, we take the first part
                val = parts[0];
            }

            // If format is YYYY-MM-DD, convert to DD-MM-YYYY
            if (typeof val === 'string' && val.includes('-')) {
                const parts = val.split('-');
                if (parts.length === 3) {
                    // Check if it's YYYY-MM-DD (first part 4 digits)
                    if (parts[0].length === 4) {
                        return `${parts[2]}-${parts[1]}-${parts[0]}`;
                    }
                }
            }
            return val;
        }

        return match[colName] || '-';
    };


    if (!isLoggedIn || (userRole !== 'editor' && userRole !== 'admin' && !(userRole === 'user' && (subRole === 'oran' || subRole === 'full')))) {
        return <Navigate to="/login" />;
    }

    // --- TabloTest Style Rendering ---
    return (
        <>
            {loading && <div className="loader">
                <div className="loader-inner">
                    <div className="circle"></div>
                </div>
            </div>}
            {/* Breadcrumb Section */}
            <div className="breadcrumb-section breadcrumb-bg">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-8 offset-lg-2 text-center">
                            <div className="breadcrumb-text">
                                <p></p>
                                <h1>Yerel Kapanış Tablosu</h1>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="bookmaker-container" style={{ marginTop: '64px' }}>
                <style>{`
                .premium-button {
                    padding: 10px 20px;
                    border: none;
                    border-radius: 10px;
                    cursor: pointer;
                    font-size: 14px;
                    font-weight: 600;
                    letter-spacing: 0.3px;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                }
                .premium-button:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(0,0,0,0.4);
                }
                .premium-button:active {
                    transform: translateY(0);
                }
                .btn-save {
                    background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
                    color: white;
                }
                .btn-save:hover {
                    background: linear-gradient(135deg, #e85a2a 0%, #e08419 100%);
                }
                .btn-delete {
                    background: linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%);
                    color: #ff6b35;
                    border: 1px solid #ff6b35;
                }
                .btn-delete:hover {
                    background: linear-gradient(135deg, #3d3d3d 0%, #2a2a2a 100%);
                }
                .btn-delete:disabled {
                    background: linear-gradient(135deg, #3d3d3d 0%, #2a2a2a 100%);
                    color: #666;
                    border-color: #444;
                    cursor: not-allowed;
                    opacity: 0.6;
                    transform: none;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
                }
                .btn-delete:disabled:hover {
                    transform: none;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
                }
                .btn-clear {
                    background: linear-gradient(135deg, #3d3d3d 0%, #2a2a2a 100%);
                    color: #ffa500;
                    border: 1px solid #ffa500;
                }
                .btn-clear:hover {
                    background: linear-gradient(135deg, #4d4d4d 0%, #3a3a3a 100%);
                }
                .btn-settings {
                    background: linear-gradient(135deg, #ff8c00 0%, #ff6600 100%);
                    color: white;
                }
                .btn-settings:hover {
                    background: linear-gradient(135deg, #e67e00 0%, #e65c00 100%);
                }
                .btn-column {
                    background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
                    color: #ff6b35;
                    border: 1px solid #ff6b35;
                }
                .btn-column:hover {
                    background: linear-gradient(135deg, #2a2a2a 0%, #3d3d3d 100%);
                }
                .premium-dropdown {
                    padding: 10px 16px;
                    border-radius: 10px;
                    border: 2px solid #ff6b35;
                    background: linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%);
                    font-size: 14px;
                    font-weight: 500;
                    color: #fff;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                    min-width: 180px;
                }
                .premium-dropdown:hover {
                    border-color: #ffa500;
                    box-shadow: 0 4px 12px rgba(255, 107, 53, 0.3);
                }
                .premium-dropdown:focus {
                    outline: none;
                    border-color: #ffa500;
                    box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.2);
                }
                .premium-dropdown option {
                    background: #1a1a1a;
                    color: #fff;
                }
                .divider {
                    width: 2px;
                    background: linear-gradient(180deg, transparent 0%, #ff6b35 50%, transparent 100%);
                    height: 35px;
                    margin: 0 8px;
                }
                .header-title {
                    background: linear-gradient(135deg, #ff6b35 0%, #ffa500 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    font-size: 28px;
                    font-weight: 700;
                    letter-spacing: -0.5px;
                    margin: 0;
                    border: none;
                }

                @media (max-width: 768px) {
                    .responsive-controls {
                        flex-direction: column !important;
                        align-items: stretch !important;
                        gap: 16px !important;
                    }
                    .responsive-left-section {
                        flex-direction: column !important;
                        width: 100% !important;
                        gap: 12px !important;
                    }
                    .responsive-right-section {
                        flex-direction: column !important;
                        width: 100% !important;
                        justify-content: stretch !important;
                        gap: 10px !important;
                    }
                    .responsive-filter-item {
                        width: 100% !important;
                    }

                    .responsive-filter-item > div {
                        width: 100% !important;
                    }
                    .premium-button {
                        width: 100%;
                        justify-content: center;
                    }
                    .premium-dropdown {
                        width: 100% !important;
                    }
                    .divider {
                        display: none;
                    }
                }

                /* Scrollbar at TOP trick */
                .table-container {
                    transform: rotateX(180deg);
                    overflow-x: auto;
                    margin-bottom: 20px;
                }
                .bookmaker-table {
                    transform: rotateX(180deg);
                    width: 100%;
                    border-collapse: collapse;
                    font-family: 'Poppins', 'Segoe UI', sans-serif;
                }
                .bookmaker-table th,
                .bookmaker-table td {
                    text-align: center;
                    vertical-align: middle;
                    padding: 6px 10px;
                    font-size: 11px;
                    min-width: 80px;
                    border: 1px solid #000;
                }
                .bookmaker-table th {
                    font-weight: 600;
                    letter-spacing: 0.3px;
                }
                .bookmaker-table td {
                    font-weight: 500;
                }
            `}</style>

                {/* Header Row with Title */}


                {/* Controls Row - Split into Left and Right */}
                <div className="responsive-controls" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px',
                    padding: '20px',
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                    borderRadius: '16px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                    gap: '20px',
                    flexWrap: 'wrap'
                }}>
                    {/* LEFT SECTION: Date + Match */}
                    <div className="responsive-left-section" style={{ display: 'flex', gap: '12px', alignItems: 'center', flex: '0 1 auto' }}>
                        {/* Date Select */}
                        <div className="responsive-filter-item" style={{ width: '180px' }}>
                            <SearchableSelect
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                options={dateOptions}
                                placeholder="�️ Tarih Seçiniz"
                                name="date"
                            />
                        </div>

                        {/* Match Select Dropdown */}
                        {selectedDate && (
                            <div className="responsive-filter-item" style={{ width: '250px' }}>
                                <SearchableSelect
                                    value={selectedMatchId}
                                    onChange={handleMatchSelect}
                                    options={matchesForDropdown.map(m => {
                                        let formattedTime = m.time;
                                        if (formattedTime && formattedTime.includes('T')) {
                                            formattedTime = formattedTime.split('T')[1].substring(0, 5);
                                        }
                                        return {
                                            value: m.id,
                                            label: `${formattedTime} | ${m.country} | ${m.league} | ${m.name}`
                                        };
                                    })}
                                    placeholder="⚽ Maç Seçiniz"
                                    name="match"
                                />
                            </div>
                        )}
                    </div>


                    {/* RIGHT SECTION: Filters + Action Buttons */}
                    <div className="responsive-right-section" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', flex: '0 1 auto' }}>
                        {/* Saved Filters Dropdown */}
                        <select
                            className="premium-dropdown"
                            value={selectedFilterId}
                            onChange={(e) => {
                                const fId = e.target.value;
                                setSelectedFilterId(fId);
                                if (fId) {
                                    checkAndApplyFilter(fId, selectedMatchId);
                                }
                            }}
                        >
                            <option value="">🎯 Filtre Seçin</option>
                            {savedFilters.map(f => (
                                <option key={f.id} value={f.id}>{f.filter_name}</option>
                            ))}
                        </select>

                        <button
                            className="premium-button btn-save"
                            onClick={handleSaveSavedFilter}
                        >
                            ⚡ Kaydet
                        </button>

                        <button
                            className="premium-button btn-delete"
                            onClick={handleDeleteSavedFilter}
                            disabled={!selectedFilterId}
                        >
                            ❌ Sil
                        </button>

                        <div className="divider"></div>

                        <button
                            className="premium-button btn-clear"
                            onClick={handleClearFilters}
                        >
                            🔄 Temizle
                        </button>

                        {/* Temporarily hidden - Tasarım Ayarları button
                        <button
                            className="premium-button btn-settings"
                            onClick={() => setShowSettings(!showSettings)}
                        >
                            {showSettings ? '⚙️ Ayarları Gizle' : '⚙️ Tasarım Ayarları'}
                        </button>
                        */}

                        {/* Temporarily hidden - Sütun Filtrele button
                        <button
                            className="premium-button btn-column"
                            onClick={openColumnModal}
                        >
                            🔧 Sütun Filtrele
                        </button>
                        */}
                    </div>
                </div>

                {/* Column Filter Side Panel */}
                {showColumnModal && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 2000,
                        pointerEvents: 'none' // Allow seeing through and maybe clicking through (optional)? No, we want to catch clicks to close usually. 
                        // But if user wants to see table, let's keep it transparent.
                        // To allow closing by clicking outside, we need a transparent layer that IS clickable.
                    }}>
                        {/* Transparent Clickable Overlay for dismissal */}
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'rgba(0,0,0,0)', // Completely transparent
                                pointerEvents: 'auto'
                            }}
                            onClick={() => setShowColumnModal(false)}
                        />

                        {/* The Right Drawer */}
                        <div className="drawer-panel" style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            bottom: 0,
                            width: '420px',
                            maxWidth: '90vw',
                            background: 'linear-gradient(180deg, #1a1a1a 0%, #2d2d2d 100%)',
                            boxShadow: '-10px 0 30px rgba(0,0,0,0.5)',
                            display: 'flex',
                            flexDirection: 'column',
                            pointerEvents: 'auto',
                            animation: 'slideInRight 0.35s cubic-bezier(0.16, 1, 0.3, 1)'
                        }}>
                            <style>{`
                            @keyframes slideInRight {
                                from { transform: translateX(100%); opacity: 0; }
                                to { transform: translateX(0); opacity: 1; }
                            }
                            .drawer-panel {
                                border-left: 2px solid #ff6b35;
                            }
                            .custom-checkbox-label {
                                display: flex;
                                align-items: center;
                                gap: 12px;
                                padding: 12px 16px;
                                cursor: pointer;
                                border-radius: 10px;
                                transition: all 0.2s ease;
                                background: rgba(255,255,255,0.05);
                                border: 1px solid rgba(255,255,255,0.1);
                                margin-bottom: 8px;
                            }
                            .custom-checkbox-label:hover {
                                background: rgba(255,107,53,0.15);
                                border-color: #ff6b35;
                                transform: translateX(-2px);
                                box-shadow: 0 4px 12px rgba(255,107,53,0.2);
                            }
                            .custom-checkbox-label.disabled {
                                opacity: 0.5;
                                cursor: not-allowed;
                                background: rgba(255,255,255,0.02);
                            }
                            .custom-checkbox-label.disabled:hover {
                                transform: none;
                                border-color: rgba(255,255,255,0.1);
                            }
                            .custom-checkbox {
                                appearance: none;
                                width: 20px;
                                height: 20px;
                                border: 2px solid #ff6b35;
                                border-radius: 6px;
                                cursor: pointer;
                                position: relative;
                                transition: all 0.2s ease;
                                background: transparent;
                                flex-shrink: 0;
                            }
                            .custom-checkbox:checked {
                                background: #ff6b35;
                                border-color: #ff6b35;
                            }
                            .custom-checkbox:checked::after {
                                content: '✓';
                                position: absolute;
                                top: 50%;
                                left: 50%;
                                transform: translate(-50%, -50%);
                                color: white;
                                font-size: 12px;
                                font-weight: bold;
                            }
                            .checkbox-text {
                                font-size: 15px;
                                color: #fff;
                                font-weight: 500;
                            }
                            
                            /* Selected Tags Box styles */
                            .selected-tags-container {
                                background: rgba(0,0,0,0.3);
                                border-top: 1px solid rgba(255,107,53,0.3);
                                padding: 16px;
                                max-height: 150px;
                                overflow-y: auto;
                            }
                            .tag {
                                display: inline-flex;
                                align-items: center;
                                padding: 4px 10px;
                                background: rgba(255,107,53,0.2);
                                color: #ff6b35;
                                border-radius: 99px;
                                font-size: 12px;
                                font-weight: 600;
                                margin: 4px;
                                border: 1px solid rgba(255,107,53,0.4);
                            }
                        `}</style>

                            {/* Drawer Header */}
                            <div style={{
                                padding: '24px',
                                borderBottom: '1px solid rgba(255,107,53,0.3)',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
                            }}>
                                <h3 style={{
                                    margin: 0,
                                    fontSize: '20px',
                                    fontWeight: '700',
                                    color: '#ff6b35',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px'
                                }}>
                                    🔧 Sütun Filtrele
                                    <span style={{
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        padding: '4px 10px',
                                        background: 'rgba(255,107,53,0.2)',
                                        borderRadius: '20px',
                                        color: '#ffa500',
                                        border: '1px solid rgba(255,107,53,0.4)'
                                    }}>
                                        {tempSelectedIds.length} Seçili
                                    </span>
                                </h3>
                                <button
                                    onClick={() => setShowColumnModal(false)}
                                    style={{
                                        background: 'transparent',
                                        border: 'none',
                                        fontSize: '24px',
                                        color: '#ff6b35',
                                        cursor: 'pointer',
                                        padding: '0 8px',
                                        transition: 'color 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.target.style.color = '#ffa500'}
                                    onMouseLeave={(e) => e.target.style.color = '#ff6b35'}
                                >
                                    ✕
                                </button>
                            </div>

                            {/* Select/Deselect All Buttons */}
                            <div style={{
                                padding: '10px 24px',
                                display: 'flex',
                                gap: '10px',
                                borderBottom: '1px solid rgba(255,255,255,0.1)',
                                background: 'rgba(0,0,0,0.2)'
                            }}>
                                <button
                                    onClick={handleSelectAll}
                                    style={{
                                        flex: 1,
                                        padding: '8px',
                                        borderRadius: '8px',
                                        border: '1px solid #ff6b35',
                                        background: 'rgba(255,107,53,0.15)',
                                        color: '#ff6b35',
                                        fontWeight: '600',
                                        fontSize: '13px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.background = 'rgba(255,107,53,0.3)';
                                        e.target.style.borderColor = '#ffa500';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.background = 'rgba(255,107,53,0.15)';
                                        e.target.style.borderColor = '#ff6b35';
                                    }}
                                >
                                    ⚡ Tümünü Seç
                                </button>
                                <button
                                    onClick={handleDeselectAll}
                                    style={{
                                        flex: 1,
                                        padding: '8px',
                                        borderRadius: '8px',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        background: 'rgba(255,255,255,0.05)',
                                        color: '#ccc',
                                        fontWeight: '600',
                                        fontSize: '13px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.background = 'rgba(255,255,255,0.1)';
                                        e.target.style.borderColor = 'rgba(255,255,255,0.3)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.background = 'rgba(255,255,255,0.05)';
                                        e.target.style.borderColor = 'rgba(255,255,255,0.2)';
                                    }}
                                >
                                    ❌ Tümünü Kaldır
                                </button>
                            </div>

                            {/* Scrollable List - Card Grid Layout */}
                            <div style={{
                                flex: 1,
                                overflowY: 'auto',
                                padding: '20px',
                                background: 'rgba(0,0,0,0.15)'
                            }}>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                                    gap: '10px'
                                }}>
                                    {allPotentialGroups.map(group => {
                                        const isSelected = tempSelectedIds.includes(intId(group.id));
                                        const isLocked = intId(group.id) === defaultGroupId;

                                        return (
                                            <div
                                                key={group.id}
                                                onClick={() => !isLocked && toggleGroupSelection(group.id)}
                                                style={{
                                                    padding: '12px 8px',
                                                    borderRadius: '10px',
                                                    cursor: isLocked ? 'default' : 'pointer',
                                                    background: isSelected
                                                        ? 'linear-gradient(135deg, rgba(255,107,53,0.2) 0%, rgba(255,165,0,0.15) 100%)'
                                                        : 'rgba(255,255,255,0.05)',
                                                    border: isSelected
                                                        ? '2px solid #ff6b35'
                                                        : '2px solid rgba(255,255,255,0.1)',
                                                    boxShadow: isSelected
                                                        ? '0 3px 8px rgba(255, 107, 53, 0.3)'
                                                        : '0 2px 4px rgba(0,0,0,0.1)',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    textAlign: 'center',
                                                    gap: '6px',
                                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    opacity: isLocked ? 0.7 : 1,
                                                    transform: isSelected ? 'translateY(-2px)' : 'none',
                                                    position: 'relative',
                                                    overflow: 'hidden'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!isLocked) {
                                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(255,107,53,0.25)';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!isLocked) {
                                                        e.currentTarget.style.transform = isSelected ? 'translateY(-2px)' : 'none';
                                                        e.currentTarget.style.boxShadow = isSelected
                                                            ? '0 3px 8px rgba(255, 107, 53, 0.3)'
                                                            : '0 2px 4px rgba(0,0,0,0.1)';
                                                    }
                                                }}
                                            >
                                                {/* Selection Indicator Dot */}
                                                {isSelected && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '6px',
                                                        right: '6px',
                                                        width: '6px',
                                                        height: '6px',
                                                        borderRadius: '50%',
                                                        background: '#ff6b35',
                                                        boxShadow: '0 0 0 2px rgba(255, 107, 53, 0.3)'
                                                    }} />
                                                )}

                                                <div style={{
                                                    fontSize: '20px',
                                                    marginBottom: '2px'
                                                }}>
                                                    {/* You could map icons here based on group text if available, or just use a generic one */}
                                                    {isSelected ? '🔥' : '⚪'}
                                                </div>

                                                <span style={{
                                                    fontSize: '12px',
                                                    fontWeight: isSelected ? '700' : '500',
                                                    color: isSelected ? '#ff6b35' : '#ccc',
                                                    lineHeight: '1.3'
                                                }}>
                                                    {group.text}
                                                </span>

                                                {isLocked && (
                                                    <span style={{
                                                        fontSize: '9px',
                                                        color: '#888',
                                                        marginTop: '2px',
                                                        fontWeight: '600',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px'
                                                    }}>
                                                        Varsayılan
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Selected Columns Visualization Box */}
                            <div className="selected-tags-container">
                                <div style={{ fontSize: '12px', fontWeight: '700', color: '#ff6b35', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Seçili Sütun Grupları
                                </div>
                                {tempSelectedIds.length > 0 ? (
                                    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                                        {allPotentialGroups
                                            .filter(g => tempSelectedIds.includes(intId(g.id)))
                                            .map(g => (
                                                <span key={g.id} className="tag">
                                                    {g.text}
                                                </span>
                                            ))
                                        }
                                    </div>
                                ) : (
                                    <div style={{ fontSize: '13px', color: '#888', fontStyle: 'italic' }}>
                                        Hiçbir sütun seçilmedi.
                                    </div>
                                )}
                            </div>

                            {/* Footer Actions */}
                            <div style={{
                                padding: '20px',
                                borderTop: '1px solid rgba(255,107,53,0.3)',
                                background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
                                display: 'flex',
                                gap: '12px'
                            }}>
                                <button
                                    onClick={() => setShowColumnModal(false)}
                                    style={{
                                        flex: 1,
                                        padding: '12px',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        background: 'rgba(255,255,255,0.05)',
                                        color: '#ccc',
                                        borderRadius: '8px',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >
                                    İptal
                                </button>
                                <button
                                    onClick={handleSaveColumnConfig}
                                    style={{
                                        flex: 2,
                                        padding: '12px',
                                        border: 'none',
                                        background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
                                        color: 'white',
                                        borderRadius: '8px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        boxShadow: '0 4px 12px rgba(255, 107, 53, 0.4)'
                                    }}
                                >
                                    Kaydet ve Uygula
                                </button>
                            </div>

                        </div>
                    </div>
                )}


                {/* Color Settings (Collapsible) */}
                {showSettings && (
                    <div style={{
                        marginBottom: '20px',
                        padding: '28px',
                        background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
                        border: '2px solid rgba(255,107,53,0.3)',
                        borderRadius: '16px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '15px'
                    }}>
                        <style>{`
                            .settings-color-wrapper {
                                display: flex;
                                flex-direction: column;
                                gap: 10px;
                                padding: 16px;
                                background: linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%);
                                border-radius: 12px;
                                border: 2px solid rgba(255,255,255,0.1);
                                transition: all 0.3s ease;
                            }
                            .settings-color-wrapper:hover {
                                border-color: #ff6b35;
                                transform: translateY(-2px);
                                box-shadow: 0 4px 12px rgba(255, 107, 53, 0.3);
                            }
                        `}</style>
                        {Object.entries(colors).map(([key, val]) => (
                            <div key={key} className="settings-color-wrapper">
                                <span style={{ fontSize: '13px', fontWeight: '600', color: '#fff' }}>{colorLabels[key]}</span>
                                <input
                                    type="color"
                                    value={val}
                                    onChange={(e) => handleColorChange(key, e.target.value)}
                                    style={{
                                        width: '100%',
                                        height: '50px',
                                        border: '3px solid rgba(255,255,255,0.2)',
                                        cursor: 'pointer',
                                        borderRadius: '8px',
                                        background: 'transparent',
                                        transition: 'all 0.3s ease'
                                    }}
                                />
                                <span style={{ fontSize: '12px', color: '#888', fontFamily: 'monospace', textAlign: 'center' }}>{val}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* ERROR / LOADING */}
                {error && <div style={{ color: 'red', textAlign: 'center', marginBottom: '10px' }}>Hata: {error}</div>}



                {/* TABLE */}
                <div className="table-container">
                    <table className="bookmaker-table" style={{
                        color: colors.rowText,
                        border: '3px solid #0a1b2a',
                        borderCollapse: 'collapse'
                    }}>
                        <thead>
                            {/* 1. Group Headers */}
                            <tr style={{ backgroundColor: colors.headerBg }}>
                                {columnGroups.map((group) => (
                                    <th
                                        key={group.id}
                                        colSpan={group.columns.length}
                                        style={{
                                            padding: '6px',
                                            color: colors.headerText,
                                            borderRight: '2px solid #F6F1AD',
                                            borderBottom: '2px solid #F6F1AD',
                                            whiteSpace: 'nowrap',
                                            textAlign: 'center',
                                            fontWeight: '700',
                                            fontSize: '14px'
                                        }}
                                    >
                                        {group.text}
                                    </th>
                                ))}
                            </tr>
                            {/* 2. Column Headers + Inputs */}
                            <tr style={{ backgroundColor: colors.headerBg }}>
                                {columnGroups.flatMap(group => group.columns).map((col) => (
                                    <th
                                        key={col.id}
                                        style={{
                                            padding: '10px',
                                            color: colors.headerText,
                                            borderRight: '2px solid #F6F1AD',
                                            borderBottom: '2px solid #F6F1AD',
                                            minWidth: col.width ? `${parseInt(col.width) + 80}px` : '180px',
                                            maxWidth: col.width ? `${parseInt(col.width) + 80}px` : '180px',
                                            verticalAlign: 'top'
                                        }}
                                    >
                                        <div style={{ marginBottom: '4px', fontSize: '11px', fontWeight: '600' }}>{col.text}</div>
                                        {col.filterable && (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%' }}>
                                                {(() => {
                                                    const currentMode = filterModes[col.name] || 'default';
                                                    const style = getFilterModeStyle(currentMode);
                                                    return (
                                                        <>
                                                            {/* Temporarily hidden - Filter Mode Dropdown
                                                            <select
                                                                value={currentMode}
                                                                onChange={(e) => handleFilterModeChange(col.name, e.target.value)}
                                                                style={{
                                                                    appearance: 'none',
                                                                    WebkitAppearance: 'none',
                                                                    padding: '4px 8px',
                                                                    fontSize: '12px',
                                                                    fontWeight: '600',
                                                                    width: '100%',
                                                                    height: '28px',
                                                                    border: style.border,
                                                                    borderRadius: '6px',
                                                                    cursor: 'pointer',
                                                                    background: style.bg,
                                                                    textAlign: 'center',
                                                                    color: style.color,
                                                                    transition: 'all 0.2s',
                                                                    outline: 'none',
                                                                    boxShadow: currentMode !== 'default' ? style.shadow : 'none'
                                                                }}
                                                            >
                                                                <option value="default" style={{ background: '#fff', color: '#000' }}>Tümü</option>
                                                                <option value="eq" style={{ background: '#d4edda', color: '#155724' }}>= Eşittir</option>
                                                                <option value="range" style={{ background: '#cce5ff', color: '#004085' }}>Aralık</option>
                                                                <option value="lt" style={{ background: '#fff3cd', color: '#856404' }}>&lt; Küçüktür</option>
                                                                <option value="gt" style={{ background: '#f8d7da', color: '#721c24' }}>&gt; Büyüktür</option>
                                                            </select>
                                                            */}

                                                            <div style={{ position: 'relative', width: '100%' }}>
                                                                <input
                                                                    type="text"
                                                                    placeholder={
                                                                        currentMode === 'range' ? '' :
                                                                            currentMode === 'lt' ? '' :
                                                                                currentMode === 'gt' ? '' :
                                                                                    currentMode === 'eq' ? '' :
                                                                                        'Ara...'
                                                                    }
                                                                    value={filters[col.name] || ''}
                                                                    onChange={(e) => handleFilterChange(col.name, e.target.value)}
                                                                    style={{
                                                                        width: '100%',
                                                                        padding: '6px 26px 6px 10px',
                                                                        borderRadius: '6px',
                                                                        border: currentMode !== 'default' ? style.border : '1px solid rgba(255,255,255,0.2)',
                                                                        backgroundColor: currentMode !== 'default' ? '#fff' : 'rgba(255,255,255,0.1)',
                                                                        color: currentMode !== 'default' ? '#495057' : 'white',
                                                                        fontSize: '12px',
                                                                        fontWeight: '500',
                                                                        transition: 'all 0.2s',
                                                                        boxShadow: currentMode !== 'default' ? style.shadow : 'none',
                                                                        height: '32px'
                                                                    }}
                                                                />
                                                                {filters[col.name] && (
                                                                    <button
                                                                        onClick={() => handleFilterChange(col.name, '')}
                                                                        style={{
                                                                            position: 'absolute',
                                                                            right: '6px',
                                                                            top: '50%',
                                                                            transform: 'translateY(-50%)',
                                                                            background: 'rgba(220, 53, 69, 0.1)',
                                                                            borderRadius: '50%',
                                                                            width: '20px',
                                                                            height: '20px',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            border: 'none',
                                                                            color: '#dc3545',
                                                                            cursor: 'pointer',
                                                                            fontSize: '11px',
                                                                            fontWeight: '700',
                                                                            padding: 0
                                                                        }}
                                                                    >
                                                                        ✕
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {(() => {
                                let displayMatches = matches;
                                if (selectedMatchId) {
                                    const foundIndex = matches.findIndex(m => String(m.sys_match_id) === String(selectedMatchId));
                                    if (foundIndex > -1) {
                                        const m = matches[foundIndex];
                                        displayMatches = [m, ...matches.filter((_, i) => i !== foundIndex)];
                                    }
                                }

                                return displayMatches.map((match, index) => {
                                    const isOdd = index % 2 !== 0;
                                    const rowBg = isOdd ? colors.rowOddBg : colors.rowEvenBg; // Swapped logic or keep? Odd/Even
                                    return (
                                        <tr
                                            key={match.sys_match_id}
                                            ref={index === matches.length - 1 ? lastMatchElementRef : null}
                                            style={{
                                                backgroundColor: rowBg,
                                                color: colors.rowText,
                                                transition: 'background-color 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.hoverBg}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = rowBg}
                                        >
                                            {columnGroups.flatMap(group => group.columns).map((col) => (
                                                <td
                                                    key={col.id}
                                                    style={{
                                                        padding: '10px',
                                                        borderRight: '1px solid rgba(246, 241, 173, 0.3)',
                                                        borderBottom: '1px solid rgba(246, 241, 173, 0.3)',
                                                        fontSize: '13px',
                                                        whiteSpace: 'nowrap',
                                                        textAlign: 'center',
                                                        cursor: 'pointer'
                                                    }}
                                                    onDoubleClick={() => {
                                                        const content = getCellContent(match, col.name);
                                                        if (content !== '-' && content !== null && content !== undefined) {
                                                            handleFilterChange(col.name, String(content));
                                                        }
                                                    }}
                                                    title="Filtrelemek için çift tıklayın"
                                                >
                                                    {getCellContent(match, col.name)}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })
                            })()
                            }
                            {matches.length === 0 && !loading && (
                                <tr>
                                    <td colSpan="100%" style={{ padding: '20px', textAlign: 'center', color: '#718096' }}>
                                        Veri bulunamadı veya seçim yapılmadı.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

            </div>
        </>
    );
};

export default YerelKapanis;
